import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import * as pdfjsLib from 'pdfjs-dist';
import './FormsInvullen.css';
import AuditModal from '../components/AuditModal'; // FORMS_V2

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js';

const RENDER_SCALE = 1.5;
const TEMPLATE_BEHEER_ROLLEN = ['super_admin', 'hoofdbeheerder', 'bedrijf_admin', 'werkvoorbereider'];

const FormsInvullen = () => {
  const { id: submissionId } = useParams();
  const navigate = useNavigate();
  const { gebruiker } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [waardes, setWaardes] = useState({});
  const [werkorderId, setWerkorderId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [klantId, setKlantId] = useState('');

  const [werkorders, setWerkorders] = useState([]);
  const [projecten, setProjecten] = useState([]);
  const [klanten, setKlanten] = useState([]);

  const [pages, setPages] = useState([]);
  const [toonGetekend, setToonGetekend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [klantNamen, setKlantNamen] = useState({}); // { fieldId: naam } voor klant-rol sig-velden
  const [unsaved, setUnsaved] = useState(false);
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(null);
  const [showMagicLinkModal, setShowMagicLinkModal] = useState(false);

  const canvasRefs = useRef({});

  const isReadonly = submission && submission.status !== 'concept';

  // FORMS_V2: ondertekenaars/workflow voor ingediende documenten
  const [wf, setWf] = useState(null);
  const [wfBusy, setWfBusy] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showVelden, setShowVelden] = useState(false);
  const laadWf = useCallback(async () => {
    try { const r = await api.get(`/forms-workflow/${submissionId}/status`); setWf(r.data); } catch { setWf(null); }
  }, [submissionId]);
  useEffect(() => { if (isReadonly) laadWf(); }, [isReadonly, laadWf]);
  const wfHerinner = async () => {
    const open = (wf?.signers || []).filter(x => x.status === 'wachten' && x.uitgenodigd_op);
    if (!open.length || !window.confirm(`Herinnering sturen naar ${open.map(x => x.naam).join(', ')}?`)) return;
    setWfBusy(true);
    try { const r = await api.post(`/forms-workflow/${submissionId}/herinner`); alert(`Verstuurd: ${(r.data?.mail_results || []).filter(m => m.success).length}/${open.length}`); await laadWf(); }
    catch (e) { alert(e.response?.data?.message || e.message); } finally { setWfBusy(false); }
  };
  const wfIntrekken = async () => {
    const reden = window.prompt('Document intrekken — reden:', 'Ingetrokken door verzender');
    if (reden === null) return;
    setWfBusy(true);
    try { await api.post(`/forms-workflow/${submissionId}/intrekken`, { reden }); await laadWf(); }
    catch (e) { alert(e.response?.data?.message || e.message); } finally { setWfBusy(false); }
  };

  const pdfToBrowser = (xPdf, yPdf, wPdf, hPdf, pageHeight) => ({
    left:   xPdf * RENDER_SCALE,
    top:    pageHeight - (yPdf + hPdf) * RENDER_SCALE,
    width:  wPdf * RENDER_SCALE,
    height: hPdf * RENDER_SCALE
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const subRes = await api.get(`/form-submissions/${submissionId}`);
        if (cancelled) return;
        const sub = subRes.data.submission;
        setSubmission(sub);
        setTemplate({ naam: sub.template_naam, id: sub.template_id });
        setFields(subRes.data.fields || []);

        const wMap = {};
        (subRes.data.values || []).forEach(v => { wMap[v.field_id] = v.waarde; });
        setWaardes(wMap);

        setWerkorderId(sub.werkorder_id || '');
        setProjectId(sub.project_id || '');
        setKlantId(sub.klant_id || '');

        Promise.all([
          api.get('/werkorders').catch(() => ({ data: { werkorders: [] } })),
          api.get('/projecten').catch(() => ({ data: { projecten: [] } })),
          api.get('/klanten').catch(() => ({ data: { klanten: [] } }))
        ]).then(([wo, pr, kl]) => {
          if (cancelled) return;
          setWerkorders(wo.data.werkorders || wo.data || []);
          setProjecten(pr.data.projecten || pr.data || []);
          setKlanten(kl.data.klanten || kl.data || []);
        });

        const token = localStorage.getItem('token');
        // Als er een getekende (gegenereerde) PDF is, toon die in plaats van de lege bron-template.
        const heeftGetekend = !!sub.gegenereerde_pdf_pad;
        setToonGetekend(heeftGetekend);
        const pdfUrl = heeftGetekend
          ? `/api/form-submissions/${submissionId}/pdf`
          : `/api/form-templates/${sub.template_id}/pdf`;
        const pdfRes = await fetch(pdfUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!pdfRes.ok) throw new Error(`PDF laden mislukt (${pdfRes.status})`);
        const buf = await pdfRes.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;

        const pageInfo = [];
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          pageInfo.push({ pageNum: p, width: viewport.width, height: viewport.height, page, viewport });
        }
        if (cancelled) return;
        setPages(pageInfo);
        setLoading(false);
        setPdfLoading(false);
      } catch (err) {
        console.error('Invul-pagina laad-fout:', err);
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Fout bij laden');
          setLoading(false);
          setPdfLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  useEffect(() => {
    pages.forEach(({ pageNum, page, viewport }) => {
      const canvas = canvasRefs.current[pageNum];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      page.render({ canvasContext: ctx, viewport }).promise.catch(e =>
        console.error(`Pagina ${pageNum} render fout:`, e)
      );
    });
  }, [pages]);

  useEffect(() => {
    const handler = (e) => { if (unsaved && !isReadonly) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [unsaved, isReadonly]);

  const setVeld = (fieldId, value) => {
    setWaardes(w => ({ ...w, [fieldId]: value }));
    setUnsaved(true);
  };

  const buildValuesPayload = () => fields.map(f => ({
    field_id: f.id,
    waarde: waardes[f.id] !== undefined ? waardes[f.id] : ''
  }));

  const handleSaveConcept = async () => {
    setSaving(true);
    try {
      await api.put(`/form-submissions/${submissionId}`, {
        values: buildValuesPayload(),
        werkorder_id: werkorderId || null,
        project_id:   projectId   || null,
        klant_id:     klantId     || null
      });
      setUnsaved(false);
    } catch (err) {
      alert('Fout bij opslaan: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const missing = fields.filter(f =>
      f.verplicht && (waardes[f.id] === undefined || waardes[f.id] === '' || waardes[f.id] === null)
    );
    if (missing.length > 0) {
      alert('Verplichte velden niet ingevuld:\n\n' + missing.map(f => `• ${f.label}`).join('\n'));
      return;
    }
    if (!window.confirm('Formulier definitief indienen? Daarna kun je het niet meer aanpassen.')) return;

    setSubmitting(true);
    try {
      await api.put(`/form-submissions/${submissionId}`, {
        values: buildValuesPayload(),
        werkorder_id: werkorderId || null,
        project_id:   projectId   || null,
        klant_id:     klantId     || null
      });
      const subRes = await api.post(`/form-submissions/${submissionId}/submit`, { klant_namen: klantNamen });
      setSubmission(subRes.data.submission);
      setUnsaved(false);
      setShowSubmittedModal(true);
    } catch (err) {
      alert('Fout bij indienen: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Concept weggooien?')) return;
    try {
      await api.delete(`/form-submissions/${submissionId}`);
      navigate('/forms');
    } catch (err) {
      alert('Fout bij verwijderen: ' + (err.response?.data?.message || err.message));
    }
  };

  const openPdf = () => {
    const token = localStorage.getItem('token');
    fetch(`/api/form-submissions/${submissionId}/pdf`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) throw new Error('PDF niet beschikbaar');
      return res.blob();
    }).then(blob => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    }).catch(err => alert(err.message));
  };

  const downloadExcel = () => {
    const token = localStorage.getItem('token');
    fetch(`/api/form-submissions/${submissionId}/excel`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) throw new Error('Excel export niet beschikbaar');
      return res.blob();
    }).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submission-${submissionId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }).catch(err => alert(err.message));
  };

  const handleBack = useCallback(() => {
    if (unsaved && !isReadonly && !window.confirm('Onopgeslagen wijzigingen — toch terug?')) return;
    // Probeer browser-back; fallback naar /forms als er geen history is
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/forms');
    }
  }, [unsaved, isReadonly, navigate]);

  const ingevuld = fields.filter(f => waardes[f.id] !== undefined && waardes[f.id] !== '' && waardes[f.id] !== null).length;
  const verplichtTotaal = fields.filter(f => f.verplicht).length;
  const verplichtIngevuld = fields.filter(f =>
    f.verplicht && waardes[f.id] !== undefined && waardes[f.id] !== '' && waardes[f.id] !== null
  ).length;

  if (loading) return <div className="fi-state">Formulier laden…</div>;
  if (error) return (
    <div className="fi-state fi-error">
      ⚠️ {error}<br /><br />
      <button className="fi-btn" onClick={() => navigate('/forms')}>Terug naar Forms</button>
    </div>
  );

  const magBeheren = TEMPLATE_BEHEER_ROLLEN.includes(gebruiker?.rol);

  return (
    <div className="forms-invullen-pdf">
      <div className="fi-header">
        <button className="fi-btn" onClick={handleBack}>← Terug</button>
        <div className="fi-title">
          <h1>{template?.naam}</h1>
          <small>
            {isReadonly ? (
              <>Status: <strong className="fi-status fi-status-ingediend">{submission.status}</strong>
                {submission.ingediend_op && ` · ${new Date(submission.ingediend_op).toLocaleString('nl-NL')}`}
              </>
            ) : (
              <>Status: <strong className="fi-status fi-status-concept">concept</strong>
                {unsaved && <span className="fi-unsaved"> · niet opgeslagen</span>}
              </>
            )}
          </small>
        </div>
        <div className="fi-toolbar">
          {magBeheren && (
            <button className="fi-btn" onClick={() => setShowMagicLinkModal(true)}>📧 Magic link</button>
          )}
          {isReadonly && submission.gegenereerde_pdf_pad && (
            <button className="fi-btn fi-btn-primary" onClick={openPdf}>📄 Bekijk PDF</button>
          )}
          {isReadonly && (
            <button className="fi-btn" onClick={downloadExcel}>📊 Excel download</button>
          )}
          {!isReadonly && (
            <>
              <button className="fi-btn fi-btn-danger" onClick={handleDelete} disabled={saving || submitting}>🗑 Weggooien</button>
              <button className="fi-btn" onClick={handleSaveConcept} disabled={saving || submitting || !unsaved}>
                {saving ? 'Opslaan…' : '💾 Concept opslaan'}
              </button>
              <button className="fi-btn fi-btn-primary" onClick={handleSubmit} disabled={saving || submitting}>
                {submitting ? 'Indienen…' : '✓ Indienen'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="fi-body">
        <div className="fi-pdf-area">
          {pdfLoading && <div className="fi-state">PDF laden…</div>}
          {pages.map(({ pageNum, width, height }) => (
            <div key={pageNum} className="fi-page-wrap">
              <div className="fi-page-label">Pagina {pageNum}</div>
              <div className="fi-page-stage" style={{ width, height }}>
                <canvas
                  ref={el => { canvasRefs.current[pageNum] = el; }}
                  className="fi-canvas"
                  width={width}
                  height={height}
                />
                <div className="fi-overlay" style={{ width, height }}>
                  {!toonGetekend && fields.filter(f => f.page === pageNum).map(f => {
                    const pos = pdfToBrowser(parseFloat(f.x), parseFloat(f.y), parseFloat(f.width), parseFloat(f.height), height);
                    return (
                      <FieldOverlay
                        key={f.id}
                        field={f}
                        pos={pos}
                        value={waardes[f.id]}
                        onChange={(v) => setVeld(f.id, v)}
                        disabled={isReadonly}
                        onSignatureClick={() => setShowSignatureModal({ fieldId: f.id, label: f.label, rol: f.rol })}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="fi-sidepanel">
          {/* FORMS_V2: paneel — concept: voortgang; ingediend: status/ondertekenaars/acties */}
          {!isReadonly && (
            <div className="fi-side-section">
              <h3>Voortgang</h3>
              <div className="fi-progress-bar">
                <div
                  className="fi-progress-fill"
                  style={{ width: fields.length > 0 ? `${(ingevuld / fields.length) * 100}%` : '0%' }}
                />
              </div>
              <p className="fi-progress-text">
                {ingevuld} van {fields.length} velden ingevuld
                {verplichtTotaal > 0 && (
                  <span className={verplichtIngevuld === verplichtTotaal ? 'fi-ok' : 'fi-warn'}>
                    <br />Verplicht: {verplichtIngevuld}/{verplichtTotaal}
                  </span>
                )}
              </p>
            </div>
          )}

          {isReadonly && (
            <div className="fi-side-section">
              <h3>Status</h3>
              {(() => {
                const ws = wf?.workflow_status || submission?.workflow_status || 'draft';
                const kl = { compleet: ['#d1fae5', '#065f46', '✅ Volledig ondertekend'], wachten_op_signers: ['#fef3c7', '#92400e', '⏳ Wacht op ondertekening'], geweigerd: ['#fee2e2', '#991b1b', '✗ Geweigerd / ingetrokken'], verlopen: ['#e2e8f0', '#475569', '⌛ Verlopen'] }[ws] || ['#dbeafe', '#1e40af', '📤 ' + (submission?.status || 'ingediend')];
                const signers = wf?.signers || [];
                const nOpen = signers.filter(x => x.status === 'wachten').length;
                const fmt = (d) => d ? new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                return (
                  <>
                    <div style={{ background: kl[0], color: kl[1], padding: '6px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{kl[2]}</div>
                    {wf?.verloopt_op && ws === 'wachten_op_signers' && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Geldig tot {fmt(wf.verloopt_op)}</div>}
                    {wf?.tekenvolgorde === 'sequentieel' && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Tekenvolgorde: een voor een</div>}
                    {signers.length > 0 && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 8px' }}>
                        {signers.map(x => {
                          const c = { getekend: ['#d1fae5', '#065f46', '✓'], geweigerd: ['#fee2e2', '#991b1b', '✗'], wachten: [x.uitgenodigd_op ? '#fef3c7' : '#f1f5f9', x.uitgenodigd_op ? '#92400e' : '#64748b', x.uitgenodigd_op ? '⏳' : '⏸'] }[x.status] || ['#f1f5f9', '#475569', '·'];
                          return (
                            <li key={x.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                              <span style={{ background: c[0], color: c[1], borderRadius: 10, padding: '1px 7px', fontWeight: 700, flexShrink: 0 }}>{c[2]}{wf?.tekenvolgorde === 'sequentieel' ? ' ' + x.volgorde : ''}</span>
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.naam}</div>
                                <div style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.email}</div>
                                <div style={{ color: '#94a3b8' }}>
                                  {x.status === 'getekend' && `getekend ${fmt(x.getekend_op)}`}
                                  {x.status === 'geweigerd' && `geweigerd ${fmt(x.geweigerd_op)}${x.weigering_reden ? ' — ' + x.weigering_reden : ''}`}
                                  {x.status === 'wachten' && (x.uitgenodigd_op ? `uitgenodigd ${fmt(x.uitgenodigd_op)}${x.aantal_herinneringen ? ` · ${x.aantal_herinneringen}× herinnerd` : ''}` : 'wacht op eerdere ondertekenaar')}
                                  {x.otp_verified_at && ' · 🔐 SMS'}
                                </div>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {ws === 'wachten_op_signers' && nOpen > 0 && (
                        <button className="fi-btn" disabled={wfBusy} onClick={wfHerinner} style={{ background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}>🔔 Herinner</button>
                      )}
                      {ws === 'wachten_op_signers' && (
                        <button className="fi-btn" disabled={wfBusy} onClick={wfIntrekken} style={{ background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' }}>⛔ Intrekken</button>
                      )}
                      <button className="fi-btn" onClick={() => setShowAudit(true)} title="Audit trail: wie, wanneer, IP, apparaat">📋 Audit</button>
                      {submission?.gegenereerde_pdf_pad && (
                        <button className="fi-btn" onClick={() => window.open('/v/' + submissionId, '_blank')} title="Publieke verificatiepagina (hash-check)">🔍 Verifieer</button>
                      )}
                    </div>
                    {wf?.pdf_hash && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8, wordBreak: 'break-all' }}>SHA-256 {String(wf.pdf_hash).slice(0, 24)}…{wf.tsa_timestamp_at ? ' · TSA ' + fmt(wf.tsa_timestamp_at) : ''}</div>}
                  </>
                );
              })()}
            </div>
          )}

          <div className="fi-side-section">
            <h3>Koppelingen {!isReadonly && <small>(optioneel)</small>}</h3>
            {isReadonly ? (
              <div style={{ fontSize: 13, color: '#334155', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {werkorderId && <div>🔧 Werkorder: <strong>{(werkorders.find(w => String(w.id) === String(werkorderId)) || {}).titel || '#' + werkorderId}</strong></div>}
                {projectId && <div>📁 Project: <strong>{(projecten.find(p => String(p.id) === String(projectId)) || {}).naam || '#' + projectId}</strong></div>}
                {klantId && <div>👤 Klant: <strong>{(klanten.find(k => String(k.id) === String(klantId)) || {}).naam || '#' + klantId}</strong></div>}
                {!werkorderId && !projectId && !klantId && <div style={{ color: '#94a3b8' }}>Niet gekoppeld. Klanten uit het systeem worden automatisch gekoppeld op e-mailadres van de ondertekenaar.</div>}
              </div>
            ) : (
              <>
                <label className="fi-side-label">
                  Werkorder
                  <select value={werkorderId} onChange={(e) => { setWerkorderId(e.target.value); setUnsaved(true); }} disabled={isReadonly}>
                    <option value="">— niet gekoppeld —</option>
                    {werkorders.map(w => (
                      <option key={w.id} value={w.id}>#{w.ordernummer || w.id} · {w.titel || w.beschrijving || '?'}</option>
                    ))}
                  </select>
                </label>
                <label className="fi-side-label">
                  Project
                  <select value={projectId} onChange={(e) => { setProjectId(e.target.value); setUnsaved(true); }} disabled={isReadonly}>
                    <option value="">— niet gekoppeld —</option>
                    {projecten.map(p => <option key={p.id} value={p.id}>{p.naam}</option>)}
                  </select>
                </label>
                <label className="fi-side-label">
                  Klant
                  <select value={klantId} onChange={(e) => { setKlantId(e.target.value); setUnsaved(true); }} disabled={isReadonly}>
                    <option value="">— niet gekoppeld —</option>
                    {klanten.map(k => <option key={k.id} value={k.id}>{k.naam}</option>)}
                  </select>
                </label>
              </>
            )}
          </div>

          <div className="fi-side-section">
            <h3 style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setShowVelden(v => !v)}>
              Veld-overzicht <small>{ingevuld}/{fields.length}</small> <span style={{ float: 'right', color: '#94a3b8' }}>{(showVelden || !isReadonly) ? '▾' : '▸'}</span>
            </h3>
            {(showVelden || !isReadonly) && (
              <ul className="fi-fieldlist">
                {fields.map(f => {
                  const filled = waardes[f.id] !== undefined && waardes[f.id] !== '' && waardes[f.id] !== null;
                  return (
                    <li key={f.id} className={filled ? 'fi-li-filled' : ''}>
                      <span className="fi-li-marker">{filled ? '✓' : '○'}</span>
                      <span className="fi-li-label">{f.label}{f.verplicht && <span className="fi-req"> *</span>}</span>
                      <small className="fi-li-meta">p{f.page}</small>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {showAudit && <AuditModal submissionId={parseInt(submissionId, 10)} onClose={() => setShowAudit(false)} />}
      {showSignatureModal && (
        <SignatureModal
          label={showSignatureModal.label}
          existing={waardes[showSignatureModal.fieldId]}
          onSave={(dataUrl, signerNaam) => {
            setVeld(showSignatureModal.fieldId, dataUrl);
            if (signerNaam) setKlantNamen(prev => ({ ...prev, [showSignatureModal.fieldId]: signerNaam }));
            setShowSignatureModal(null);
          }}
          rol={showSignatureModal.rol}
          onClose={() => setShowSignatureModal(null)}
        />
      )}

      {showSubmittedModal && (
        <div className="fi-modal-overlay">
          <div className="fi-modal">
            <h2>✅ Formulier ingediend</h2>
            <button onClick={() => setShowSubmittedModal(false)} style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#94a3b8" }} title="Sluiten">×</button>
            <p>Het formulier is succesvol ingediend en de PDF is gegenereerd.</p>
            <div className="fi-modal-acties">
              <button className="fi-btn" onClick={() => navigate('/forms')}>Naar lijst</button>
              <button className="fi-btn fi-btn-primary" onClick={() => { setShowSubmittedModal(false); openPdf(); }}>
                📄 PDF openen
              </button>
            </div>
          </div>
        </div>
      )}

      {showMagicLinkModal && (
        <MagicLinkModal
          submissionId={submissionId}
          submissionStatus={submission?.status}
          onClose={() => setShowMagicLinkModal(false)}
        />
      )}
    </div>
  );
};

const FieldOverlay = ({ field, pos, value, onChange, disabled, onSignatureClick }) => {
  const style = { left: pos.left, top: pos.top, width: pos.width, height: pos.height };
  const filled = value !== undefined && value !== '' && value !== null;

  if (field.type === 'textarea') {
    return (
      <textarea
        style={style}
        className={`fi-overlay-input fi-overlay-textarea ${filled ? 'filled' : ''}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        title={field.label}
        placeholder={field.label}
      />
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label className={`fi-overlay-checkbox ${filled ? 'filled' : ''}`} style={style} title={field.label}>
        <input
          type="checkbox"
          checked={value === 'true' || value === true}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          disabled={disabled}
        />
      </label>
    );
  }

  if (field.type === 'signature' || field.type === 'paraaf') {
    return (
      <button
        type="button"
        style={style}
        className={`fi-overlay-input fi-overlay-signature ${filled ? 'filled' : ''}`}
        onClick={(e) => { e.preventDefault(); if (!disabled) onSignatureClick(); }}
        disabled={disabled}
        title={field.label}
      >
        {filled ? <img src={value} alt="handtekening" /> : <span>✍ {field.label}</span>}
      </button>
    );
  }

  if (field.type === 'dropdown' || field.type === 'radio') {
    const choices = field.opties?.choices || [];
    return (
      <select
        style={style}
        className={`fi-overlay-input ${filled ? 'filled' : ''}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        title={field.label}
      >
        <option value="">— {field.label} —</option>
        {choices.map((c, i) => <option key={i} value={c}>{c}</option>)}
      </select>
    );
  }

  if (field.type === 'meetwaarde') {
    const eenheid = field.opties?.eenheid;
    return (
      <div style={style} className={`fi-overlay-meetwaarde ${filled ? 'filled' : ''}`} title={field.label}>
        <input
          type="number"
          step="any"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={field.label}
        />
        {eenheid && <span className="fi-overlay-eenheid">{eenheid}</span>}
      </div>
    );
  }

  const inputType = ['number', 'date', 'time'].includes(field.type) ? field.type : 'text';
  return (
    <input
      type={inputType}
      style={style}
      className={`fi-overlay-input ${filled ? 'filled' : ''}`}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      title={field.label}
      placeholder={field.label}
    />
  );
};

const SignatureModal = ({ label, existing, onSave, onClose, rol }) => {
  const [signerNaam, setSignerNaam] = useState('');
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(!!existing);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !existing) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setHasContent(true);
    };
    img.src = existing;
  }, [existing]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };

  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(x, y);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e3a5f';
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => { if (drawing) { setDrawing(false); setHasContent(true); } };

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasContent(false);
  };

  const save = () => {
    if (!hasContent) { alert('Zet eerst een handtekening'); return; }
    if (rol === 'klant' && !signerNaam.trim()) { alert('Vul de naam van de ondertekenaar in'); return; }
    onSave(canvasRef.current.toDataURL('image/png'), rol === 'klant' ? signerNaam.trim() : null);
  };

  return (
    <div className="fi-modal-overlay">
      <div className="fi-modal fi-modal-signature" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
        <h2>Handtekening: {label}</h2>
            <button onClick={() => onClose()} style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#94a3b8" }} title="Sluiten">×</button>
        {rol === 'klant' && (
          <div style={{ background: '#fef3c7', padding: 12, borderRadius: 6, marginBottom: 12, border: '1px solid #f59e0b' }}>
            <strong>📱 Geef de tablet aan de klant.</strong>
            <input
              type="text"
              placeholder="Naam ondertekenaar"
              value={signerNaam}
              onChange={e => setSignerNaam(e.target.value)}
              style={{ width: '100%', padding: 8, marginTop: 8, fontSize: 16, boxSizing: 'border-box' }}
              autoFocus
            />
          </div>
        )}
        <p>Teken hieronder je handtekening (muis of vinger op tablet).</p>
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          className="fi-signature-canvas-modal"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <div className="fi-modal-acties">
          <button className="fi-btn" onClick={clear}>Wissen</button>
          <div style={{ flex: 1 }} />
          <button className="fi-btn" onClick={onClose}>Annuleren</button>
          <button className="fi-btn fi-btn-primary" onClick={save}>Opslaan</button>
        </div>
      </div>
    </div>
  );
};

const MagicLinkModal = ({ submissionId, submissionStatus, onClose }) => {
  const [actie, setActie] = useState('invullen');
  const [email, setEmail] = useState('');
  const [naam, setNaam] = useState('');
  const [geldigDagen, setGeldigDagen] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(true);

  const defaultDagen = { invullen: 7, ondertekenen: 14, bekijken: 30 };

  const loadTokens = useCallback(async () => {
    setTokensLoading(true);
    try {
      const r = await api.get(`/form-submission-tokens?submission_id=${submissionId}`);
      setTokens(r.data?.tokens || []);
    } catch (e) {
      console.error('Tokens laden fout', e);
    } finally {
      setTokensLoading(false);
    }
  }, [submissionId]);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const handleSubmit = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert('Vul een geldig e-mailadres in');
      return;
    }
    if (actie === 'ondertekenen' && submissionStatus === 'concept') {
      alert('Submission moet eerst ingediend worden voordat een ondertekenlink mogelijk is.');
      return;
    }
    if (actie === 'bekijken' && !['ingediend', 'goedgekeurd'].includes(submissionStatus)) {
      alert('Bekijklink alleen mogelijk voor ingediende of goedgekeurde formulieren.');
      return;
    }

    setLoading(true);
    try {
      const body = {
        submission_id: submissionId,
        actie,
        ontvanger_email: email,
        ontvanger_naam: naam || undefined
      };
      if (geldigDagen) body.geldig_dagen = parseInt(geldigDagen, 10);

      const r = await api.post('/form-submission-tokens', body);
      if (r.data?.success) {
        alert(r.data.mail_verstuurd
          ? `✅ Magic link verstuurd naar ${email}`
          : `⚠️ Link aangemaakt, maar mail mislukte: ${r.data.mail_error}`);
        setEmail('');
        setNaam('');
        loadTokens();
      } else {
        alert(r.data?.message || 'Onbekende fout');
      }
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleIntrekken = async (tokenId) => {
    if (!window.confirm('Weet je zeker dat je deze link wil intrekken? Hij werkt daarna niet meer.')) return;
    try {
      await api.delete(`/form-submission-tokens/${tokenId}`);
      loadTokens();
    } catch (e) {
      alert('Intrekken mislukt: ' + (e.response?.data?.message || e.message));
    }
  };

  const tokenStatus = (t) => {
    if (!t.actief) return t.gebruikt_op ? '✅ Gebruikt' : '🚫 Ingetrokken';
    if (new Date(t.geldig_tot) < new Date()) return '⏰ Verlopen';
    return '🟢 Actief';
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="fi-modal-overlay">
      <div className="fi-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, width: '90%' }}>
        <h2>📧 Magic Link versturen</h2>
        <p style={{ color: '#64748b', marginTop: 0 }}>
          Stuur een unieke link per e-mail naar iemand die het formulier moet invullen, ondertekenen of bekijken — zonder dat ze hoeven in te loggen.
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Actie</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { v: 'invullen', label: '📝 Invullen', disabled: submissionStatus !== 'concept' },
              { v: 'ondertekenen', label: '✍️ Ondertekenen', disabled: submissionStatus === 'concept' },
              { v: 'bekijken', label: '👁️ Bekijken', disabled: !['ingediend', 'goedgekeurd'].includes(submissionStatus) }
            ].map(opt => (
              <label key={opt.v} style={{ flex: 1, minWidth: 140, padding: 10, border: `2px solid ${actie === opt.v ? '#1e3a5f' : '#e2e8f0'}`, borderRadius: 6, cursor: opt.disabled ? 'not-allowed' : 'pointer', opacity: opt.disabled ? 0.4 : 1, textAlign: 'center', background: actie === opt.v ? '#eff6ff' : 'white' }}>
                <input
                  type="radio"
                  name="ml-actie"
                  value={opt.v}
                  checked={actie === opt.v}
                  onChange={() => !opt.disabled && setActie(opt.v)}
                  disabled={opt.disabled}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>E-mail ontvanger *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="naam@voorbeeld.nl"
            style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Naam ontvanger (optioneel)</label>
          <input
            type="text"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            placeholder="Bijv. Jan de Vries"
            style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Geldig (dagen) — leeg = standaard {defaultDagen[actie]}d
          </label>
          <input
            type="number"
            min={1}
            max={365}
            value={geldigDagen}
            onChange={(e) => setGeldigDagen(e.target.value)}
            placeholder={String(defaultDagen[actie])}
            style={{ width: 120, padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>

        <div className="fi-modal-acties" style={{ marginBottom: 24 }}>
          <button className="fi-btn" onClick={onClose} disabled={loading}>Sluiten</button>
          <div style={{ flex: 1 }} />
          <button className="fi-btn fi-btn-primary" onClick={handleSubmit} disabled={loading || !email}>
            {loading ? 'Versturen...' : '📨 Verstuur'}
          </button>
        </div>

        <h3 style={{ marginTop: 8, fontSize: 16 }}>Bestaande links voor deze submission</h3>
        {tokensLoading ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>Laden...</div>
        ) : tokens.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 13, fontStyle: 'italic' }}>Nog geen links aangemaakt.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                <th style={{ padding: 6 }}>Actie</th>
                <th style={{ padding: 6 }}>Ontvanger</th>
                <th style={{ padding: 6 }}>Geldig tot</th>
                <th style={{ padding: 6 }}>Status</th>
                <th style={{ padding: 6 }}></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 6 }}>{t.actie}</td>
                  <td style={{ padding: 6 }}>
                    {t.ontvanger_email}
                    {t.ontvanger_naam && <div style={{ color: '#64748b', fontSize: 11 }}>{t.ontvanger_naam}</div>}
                  </td>
                  <td style={{ padding: 6 }}>{fmtDate(t.geldig_tot)}</td>
                  <td style={{ padding: 6 }}>{tokenStatus(t)}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>
                    {t.actief && new Date(t.geldig_tot) > new Date() && !t.gebruikt_op && (
                      <button
                        onClick={() => handleIntrekken(t.id)}
                        style={{ padding: '4px 8px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                      >
                        Intrekken
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FormsInvullen;
